import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useFeeConfig } from "../hooks/useFeeConfig";
import { showToast } from "../../../../core/utils/alert";
import { useCenter } from "../../../peoples/centers/hooks/useCenter";

interface FeeConfigModalProps {
  type?: "config";
  id?: string | null;
  courseId?: number;
  attachedCenters?: {
    id: number;
    name: string;
    code: string;
    type: string;
    address: string;
  }[];
}

const FeeConfigModal: React.FC<FeeConfigModalProps> = ({
  id,
  attachedCenters = [],
  courseId,
}) => {
  const { attachCenters, detachCenters, deleteFeeConfig } = useFeeConfig();
  const { useCenterList } = useCenter({ skip: true });
  const { data: centerResponse } = useCenterList();
  const centers = centerResponse?.data?.items || [];
  const [selectedAttachCenters, setSelectedAttachCenters] = useState<number[]>(
    []
  );
  const [selectedDetachCenters, setSelectedDetachCenters] = useState<number[]>(
    []
  );

  const handleDelete = async () => {
    if (id) {
      const result = await deleteFeeConfig(id);
      if (result.success) {
        showToast({
          title: "Success",
          text: result.data?.message || "Record deleted successfully",
          icon: "success",
        });
      } else {
        showToast({
          title: "Error",
          text: result.error || "Could not delete the record",
          icon: "error",
        });
      }
    }
  };

  const handleAttach = async () => {
    if (selectedAttachCenters.length > 0) {
      const result = await attachCenters(id!, selectedAttachCenters);
      if (result.success) {
        showToast({
          title: "Success",
          text: `${selectedAttachCenters.length} center(s) attached successfully`,
          icon: "success",
        });
        setSelectedAttachCenters([]);
      } else {
        showToast({
          title: "Error",
          text: result.error || "Could not attach centers",
          icon: "error",
        });
      }
    }
  };

  const handleDetach = async () => {
    if (selectedDetachCenters.length > 0) {
      const result = await detachCenters(id!, selectedDetachCenters);
      if (result.success) {
        showToast({
          title: "Success",
          text: "Centers detached successfully",
          icon: "success",
        });
        setSelectedDetachCenters([]);
      } else {
        showToast({
          title: "Error",
          text: result.error || "Could not detach centers",
          icon: "error",
        });
      }
    }
  };

  const attachableCenters = useMemo(() => {
    const safeAttachedCenters = Array.isArray(attachedCenters) ? attachedCenters : [];
    return centers.filter(
      (c) => !safeAttachedCenters.some((ac: any) => ac.id === c.id)
    );
  }, [centers, attachedCenters]);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allIds = attachableCenters.map((c) => c.id);
      setSelectedAttachCenters((prev) => Array.from(new Set([...prev, ...allIds])));
    } else {
      const attachableIds = attachableCenters.map((c) => c.id);
      setSelectedAttachCenters((prev) =>
        prev.filter((id) => !attachableIds.includes(id))
      );
    }
  };

  const isAllSelected =
    attachableCenters.length > 0 &&
    attachableCenters.every((c) => selectedAttachCenters.includes(c.id));

  return (
    <>
      {/* Delete Fee Configuration Modal */}
      <div className="modal custom-modal fade" id="delete-modal" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-0 m-0 justify-content-end">
              <button
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body">
              <div className="success-message text-center">
                <div className="success-popup-icon">
                  <i className="ti ti-trash" />
                </div>
                <h3>Confirm Deletion</h3>
                <p className="del-info">
                  You want to delete this fee configuration, this can't be undone
                  once you delete.
                </p>
                <div className="col-lg-12 text-center modal-btn">
                  <Link
                    to="#"
                    className="btn btn-light"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </Link>
                  <Link
                    to="#"
                    className="btn btn-danger"
                    data-bs-dismiss="modal"
                    onClick={handleDelete}
                  >
                    Yes, Delete
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Attach Modal */}
      <div className="modal custom-modal fade" id="attach-modal" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Attach Centers</h5>
              <div className="d-flex align-items-center mod-toggle">
                <button
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
            </div>
            <div className="modal-body">
              {centers.every(
                (center) => attachedCenters.some((ac: any) => ac.id === center.id)
              ) ? (
                <div className="alert alert-info">
                  <i className="ti ti-info-circle me-2"></i>
                  All centers are already attached to this course fee
                  configuration.
                </div>
              ) : (
                <>
                  {centers.some(
                    (center) => attachedCenters.some((ac: any) => ac.id === center.id)
                  ) && (
                    <div className="alert alert-warning mb-3">
                      <i className="ti ti-alert-circle me-2"></i>
                      Selected centers have existing fee configurations
                    </div>
                  )}

                  <div className="center-list-container text-start p-0 rounded border">
                    <div className="table-responsive custom-table">
                      <table className="table table-hover table-center mb-0">
                        <thead className="thead-light">
                          <tr>
                            <th style={{ width: '50px' }}>
                              <div className="form-check form-check-md">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="select-all-centers"
                                  checked={isAllSelected}
                                  onChange={handleSelectAll}
                                  disabled={attachableCenters.length === 0}
                                />
                              </div>
                            </th>
                            <th>Center Name</th>
                            <th className="text-end">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {centers.length === 0 ? (
                            <tr>
                              <td colSpan={3} className="text-center p-4 text-muted">
                                <i className="ti ti-building fs-24 mb-2 d-block text-gray-400"></i>
                                No centers available
                              </td>
                            </tr>
                          ) : (
                            centers.map((center) => {
                              const isAlreadyAttached = attachedCenters.some(
                                (ac: any) => ac.id === center.id
                              );
                              const isSelected = selectedAttachCenters.includes(center.id);
                              
                              return (
                                <tr 
                                  key={center.id} 
                                  className={`${isAlreadyAttached ? 'bg-light' : ''} ${isSelected && !isAlreadyAttached ? 'table-primary' : ''}`}
                                >
                                  <td>
                                    <div className="form-check form-check-md">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`attach-${center.id}`}
                                        value={center.id}
                                        onChange={(e) => {
                                          if (!isAlreadyAttached) {
                                            const value = Number(e.target.value);
                                            setSelectedAttachCenters((prev) =>
                                              e.target.checked
                                                ? [...prev, value]
                                                : prev.filter((id) => id !== value)
                                            );
                                          }
                                        }}
                                        checked={isAlreadyAttached || isSelected}
                                        style={isAlreadyAttached ? { pointerEvents: 'none', opacity: 0.65 } : {}}
                                      />
                                    </div>
                                  </td>
                                  <td>
                                    <label htmlFor={`attach-${center.id}`} className="d-block cursor-pointer mb-0 fw-medium text-dark">
                                      {center.name}
                                    </label>
                                  </td>
                                  <td className="text-end">
                                    {isAlreadyAttached ? (
                                      <span className="badge badge-soft-info">
                                        <i className="ti ti-check me-1"></i> Attached
                                      </span>
                                    ) : (
                                      <span className="badge badge-soft-secondary">
                                        Unattached
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="modal-btn text-end mt-4">
                    <Link
                      to="#"
                      className="btn btn-light me-2"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </Link>
                    <Link
                      to="#"
                      className="btn btn-primary"
                      data-bs-dismiss="modal"
                      style={{
                        opacity: selectedAttachCenters.length === 0 ? 0.5 : 1,
                      }}
                      onClick={(e) => {
                        if (selectedAttachCenters.length === 0) {
                          e.preventDefault();
                        } else {
                          handleAttach();
                        }
                      }}
                    >
                      Attach
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Detach Modal */}
      <div className="modal custom-modal fade" id="detach-modal" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Detach Centers</h5>
              <div className="d-flex align-items-center mod-toggle">
                <button
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
            </div>
            <div className="modal-body">
              {!Array.isArray(attachedCenters) ||
              attachedCenters.length === 0 ? (
                <div className="alert alert-warning">
                  <i className="ti ti-info-circle me-2"></i>
                  No centers are attached to this course fee configuration.
                </div>
              ) : (
                <>
                  <div className="center-list-container text-start p-0 rounded border">
                    <div className="table-responsive custom-table">
                      <table className="table table-hover table-center mb-0">
                        <thead className="thead-light">
                          <tr>
                            <th style={{ width: '50px' }}>
                              <div className="form-check form-check-md">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="select-all-detach-centers"
                                  checked={attachedCenters.length > 0 && selectedDetachCenters.length === attachedCenters.length}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedDetachCenters(attachedCenters.map((c) => c.id));
                                    } else {
                                      setSelectedDetachCenters([]);
                                    }
                                  }}
                                  disabled={attachedCenters.length === 0}
                                />
                              </div>
                            </th>
                            <th>Center Name</th>
                            <th className="text-end">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {attachedCenters.map((center) => {
                            const isSelected = selectedDetachCenters.includes(center.id);
                            return (
                              <tr 
                                key={center.id}
                                className={isSelected ? 'table-danger' : ''}
                              >
                                <td>
                                  <div className="form-check form-check-md">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`detach-${center.id}`}
                                      value={center.id}
                                      onChange={(e) => {
                                        const value = Number(e.target.value);
                                        setSelectedDetachCenters((prev) =>
                                          e.target.checked
                                            ? [...prev, value]
                                            : prev.filter((id) => id !== value)
                                        );
                                      }}
                                      checked={isSelected}
                                    />
                                  </div>
                                </td>
                                <td>
                                  <label htmlFor={`detach-${center.id}`} className="d-block cursor-pointer mb-0 fw-medium text-dark">
                                    {center.name}
                                  </label>
                                </td>
                                <td className="text-end">
                                  <span className="badge badge-soft-info">
                                    <i className="ti ti-link me-1"></i> Attached
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="modal-btn text-end mt-4">
                    <Link
                      to="#"
                      className="btn btn-light me-2"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </Link>
                    <Link
                      to="#"
                      className="btn btn-danger"
                      data-bs-dismiss="modal"
                      style={{
                        opacity: selectedDetachCenters.length === 0 ? 0.5 : 1,
                      }}
                      onClick={(e) => {
                        if (selectedDetachCenters.length === 0) {
                          e.preventDefault();
                        } else {
                          handleDetach();
                        }
                      }}
                    >
                      Detach
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .center-list-container {
          max-height: 300px;
          overflow-y: auto;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 5px;
        }
      `}</style>
    </>
  );
};

export default FeeConfigModal;
